import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { analytics } from '../services/analytics';
import SEO from '../components/SEO';

// Icons
const CheckIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const ZapIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

const WindowIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect width="18" height="18" x="3" y="3" rx="2" ry="2" />
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="12" y1="3" x2="12" y2="21" />
  </svg>
);

const XIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const WindowDoorQuotingSoftware = () => {
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  
  // Structured data for Windows & Doors page
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Product", 
    "name": "Window & Door Quoting Software by QuoteTier",
    "description": "Best quoting software for window and door companies. Send interactive quotes in minutes and win 78% more jobs by being first.",
    "category": "Software",
    "brand": {
      "@type": "Brand",
      "name": "QuoteTier"
    },
    "offers": {
      "@type": "Offer",
      "name": "Free Trial",
      "price": "0",
      "priceCurrency": "USD"
    },
    "audience": {
      "@type": "Audience",
      "audienceType": "Window Installers, Door Companies, Glaziers"
    }
  };

  useEffect(() => {
    analytics.track('WindowDoor Landing Page View');
  }, []);

  useEffect(() => {
    const controlHeader = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY < lastScrollY || currentScrollY < 100) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', controlHeader);
    return () => window.removeEventListener('scroll', controlHeader);
  }, [lastScrollY]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO 
        title="Best Window & Door Quoting Software | Glazing Estimating"
        description="78% of customers buy from the first company to quote. Use QuoteTier to send Good/Better/Best window and door quotes in under 5 minutes."
        canonical="/window-door-quoting-software"
        structuredData={structuredData}
        ogTitle="Window Quoting Software - Win 78% More Jobs"
        ogDescription="Speed wins. Create beautiful, interactive quotes for vinyl, fiberglass, and wood windows. Let clients sign instantly on their phone."
        noindex={true}
      />
      {/* Header */}
      <header 
        className={`fixed top-4 left-1/2 z-50 w-[90%] max-w-4xl -translate-x-1/2 rounded-2xl border border-black/10 bg-white/95 shadow-lg transition-all duration-300 ease-in-out ${
          isVisible ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'
        }`}
      >
        <div className="flex h-14 items-center justify-between px-4">
          <div className="flex items-center">
            <ZapIcon className="h-4 w-4 mr-2 text-primary" />
            <span className="font-instrument text-xl md:text-2xl">QuoteTier</span>
          </div>
          
          <nav className="hidden md:flex items-center space-x-6 text-sm font-medium">
            <Link to="/blog/" className="font-instrument text-black text-xl hover:text-gray-700 transition-colors">
              Blog
            </Link>
            <Link to="/login" className="font-instrument text-black text-xl hover:text-gray-700 transition-colors">
              Sign In
            </Link>
            <Link to="/signup" onClick={() => analytics.track('CTA Clicked', { location: 'navigation', action: 'signup' })}>
              <Button className="font-instrument text-xl px-4 py-2 bg-black text-white hover:bg-gray-800 rounded-lg">
                Start Free
              </Button>
            </Link>
          </nav>
          
          <div className="md:hidden flex items-center space-x-2">
            <Link to="/blog/" className="font-instrument text-sm px-3 py-2 text-black hover:text-gray-700 transition-colors">
              Blog
            </Link>
            <Link to="/login" className="font-instrument text-sm px-3 py-2 text-black hover:text-gray-700 transition-colors">
              Sign In
            </Link>
            <Link to="/signup" onClick={() => analytics.track('CTA Clicked', { location: 'mobile-navigation', action: 'signup' })}>
              <Button className="font-instrument text-sm px-3 py-2 bg-black text-white hover:bg-gray-800 rounded-lg">
                Free
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="pt-20">
        {/* Hero Section */}
        <section className="py-32 md:py-40 lg:py-48">
          <div className="container max-w-7xl mx-auto px-4">
            <div className="text-center max-w-4xl mx-auto">
              <h1 className="font-instrument text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tight leading-tight mb-6">
                Window & Door Quoting Software <span className="text-primary font-bold">Built for Speed</span>
              </h1>
              <p className="font-jetbrains text-xl md:text-2xl text-gray-600 tracking-tight leading-relaxed mb-8">
                78% of homeowners buy from the first company to quote. Stop using Word docs. Send interactive Vinyl/Composite/Wood options in minutes.
              </p>
              <div className="font-instrument mt-12 flex flex-col sm:flex-row gap-6 justify-center items-center mb-6">
                <Link to="/signup" onClick={() => analytics.track('CTA Clicked', { location: 'window-hero', action: 'signup' })}>
                  <Button className="px-8 py-4 text-xl font-semibold bg-black text-white hover:bg-gray-800 rounded-lg shadow-lg">
                    Start Free — 3 Quotes/Mo
                  </Button>
                </Link>
                <Link to="/demo-quote/window" onClick={() => analytics.track('Demo Clicked', { location: 'window-hero', industry: 'window' })}>
                  <Button className="px-8 py-4 text-xl font-semibold border-2 border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg" variant="outline">
                    See Window Quote Demo →
                  </Button>
                </Link>
              </div>
              <p className="font-jetbrains mt-10 text-lg text-gray-500">
                No credit card required • Setup in 5 minutes • Works on mobile
              </p>
            </div>
          </div>
        </section>

        {/* Window Value Props */}
        <section className="py-16 md:py-24 bg-gray-50">
          <div className="container max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="font-instrument text-3xl md:text-4xl font-bold mb-4">Why Speed Matters in Window Sales</h2>
              <p className="font-jetbrains text-lg text-gray-600">The average window job is $1,730+. Don't lose it to a slow quote.</p>
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              <Card className="border border-black">
                <CardHeader>
                  <CardTitle className="font-instrument text-xl">Be The First To Quote</CardTitle>
                  <CardDescription className="font-jetbrains">Statistics show 78% of jobs go to the first contractor who submits a formal proposal. Our mobile app lets you quote from the driveway.</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 font-jetbrains text-sm">
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Create quotes in under 5 minutes</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Send via text or email instantly</li>
                  </ul>
                </CardContent>
              </Card>
              <Card className="border border-black">
                <CardHeader>
                  <CardTitle className="font-instrument text-xl">Good / Better / Best Series</CardTitle>
                  <CardDescription className="font-jetbrains">Stop overwhelming customers with catalogs. Present a clear "Builder Grade", "Mid-Range", and "Premium Architectural" option.</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 font-jetbrains text-sm">
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Simplify decision making</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Increase average ticket size</li>
                  </ul>
                </CardContent>
              </Card>
              <Card className="border border-black">
                <CardHeader>
                  <CardTitle className="font-instrument text-xl">Energy Efficiency Upsells</CardTitle>
                  <CardDescription className="font-jetbrains">Make it easy to add Triple Pane glass, Low-E coatings, or Argon gas as optional upgrades.</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 font-jetbrains text-sm">
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Educate clients on ROI</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Boost margins with premium glass</li>
                  </ul>
                </CardContent>
              </Card>
              <Card className="border border-black">
                <CardHeader>
                  <CardTitle className="font-instrument text-xl">Visual Proposals</CardTitle>
                  <CardDescription className="font-jetbrains">Windows are a visual purchase. Include photos of your past installs or product brochures directly in the quote.</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 font-jetbrains text-sm">
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Build trust with examples</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Professional branding</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Comparison Table */}
        <section className="py-16 md:py-24">
          <div className="container max-w-5xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="font-instrument text-3xl md:text-4xl font-bold mb-4">
                QuoteTier vs Manufacturer Software
              </h2>
              <p className="font-jetbrains text-lg text-gray-600">
                Don't get locked into one brand's clunky tool.
              </p>
            </div>
            
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-6 px-6 font-instrument text-lg font-semibold text-gray-900 bg-gray-50">
                      Feature
                    </th>
                    <th className="text-center py-6 px-6 bg-black text-white">
                      <div className="flex flex-col items-center gap-2">
                        <span className="font-instrument text-lg font-bold">QuoteTier</span>
                      </div>
                    </th>
                    <th className="text-center py-6 px-6 font-instrument text-lg font-semibold text-gray-700 bg-gray-50">
                      Manufacturer Tools
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-100">
                    <td className="py-4 px-6 font-jetbrains font-medium">Quote Multiple Brands</td>
                    <td className="py-4 px-6 text-center"><CheckIcon className="h-5 w-5 mx-auto" /></td>
                    <td className="py-4 px-6 text-center"><XIcon className="h-5 w-5 text-gray-300 mx-auto" /></td>
                  </tr>
                  <tr className="border-b border-gray-100 bg-gray-50/30">
                    <td className="py-4 px-6 font-jetbrains font-medium">Customer-Friendly Interface</td>
                    <td className="py-4 px-6 text-center"><CheckIcon className="h-5 w-5 mx-auto" /></td>
                    <td className="py-4 px-6 text-center"><XIcon className="h-5 w-5 text-gray-300 mx-auto" /></td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="py-4 px-6 font-jetbrains font-medium">Sales Tracking (Views/Opens)</td>
                    <td className="py-4 px-6 text-center"><CheckIcon className="h-5 w-5 mx-auto" /></td>
                    <td className="py-4 px-6 text-center"><XIcon className="h-5 w-5 text-gray-300 mx-auto" /></td>
                  </tr>
                  <tr className="border-b border-gray-100 bg-gray-50/30">
                    <td className="py-4 px-6 font-jetbrains font-medium">Setup Time</td>
                    <td className="py-4 px-6 text-center">
                      <span className="font-jetbrains font-semibold text-gray-800 bg-gray-100 px-3 py-1 rounded-full text-sm">
                        Minutes
                      </span>
                    </td>
                    <td className="py-4 px-6 text-center">
                      <span className="font-jetbrains text-gray-600 text-sm">Weeks</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-16 md:py-24 bg-gradient-to-r from-gray-900 to-black text-white">
          <div className="container max-w-4xl mx-auto px-4 text-center">
            <h2 className="font-instrument text-4xl md:text-5xl font-bold mb-6">
              Ready to Close 78% More Window Jobs?
            </h2>
            <p className="font-jetbrains text-xl text-gray-200 mb-8">
              Join window and door professionals who are winning more jobs by being faster and looking better.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-8">
              <Link to="/signup" onClick={() => analytics.track('CTA Clicked', { location: 'window-final-cta', action: 'signup' })}>
                <Button className="px-10 py-4 font-instrument text-xl font-semibold bg-black text-white hover:bg-gray-800 rounded-lg shadow-lg">
                  Start Free — 3 Quotes/Mo
                </Button>
              </Link>
              <Link to="/demo-quote/window" onClick={() => analytics.track('Demo Clicked', { location: 'window-final-cta', industry: 'window' })}>
                <Button className="px-8 py-4 text-lg font-semibold border-2 border-white text-white hover:bg-white/10 rounded-lg" variant="outline">
                  Try Interactive Demo First →
                </Button>
              </Link>
            </div>
            
            <p className="font-jetbrains text-sm text-gray-300">
              No credit card required • Cancel anytime • Setup in minutes
            </p>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t">
        <div className="container max-w-7xl mx-auto px-4 py-8 flex flex-col md:flex-row justify-between items-center">
          <p className="text-sm text-muted-foreground">&copy; 2025 QuoteTier. All rights reserved. <span className="footer-credit">Site by <a href="https://novanorthstudio.com/?utm_source=quotetier.com&utm_medium=referral&utm_campaign=site-credit">Nova North Studio</a></span></p>
          <div className="flex gap-4 mt-4 md:mt-0">
            <Link to="/terms" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Terms</Link>
            <Link to="/privacy" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Privacy</Link>
            <a href="mailto:info@quotetier.com" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default WindowDoorQuotingSoftware;
